import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Row, Col, Container, Card, CardBody } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Grid, Select } from '@mantine/core';
import UiContent from '../../../../../Components/Common/UiContent';
import BreadCrumb from '../../../../../Components/Common/BreadCrumb';
import { clearDashboardData } from '../../../../../slices/fuelSupply/reducer';
import { clearDashboardData as clearFuelTankData } from '../../../../../slices/fuelTank/reducer';
import HourMeterManagementEChart from '../Widgets/HourMeterManagementEChart';
import ConsumptionsManagementEChart from '../Widgets/ConsumptionsManagementEChart';
import EmissionsManagementEChart from '../Widgets/EmissionsManagementEChart';
import { getCompanyWithHourMeterList } from '../../../../../helpers/establishment/backend_helper';
import { ConsumptionsManagementMap } from '../Widgets/ConsumptionsManagementMap';

const ManagementFuelDashboard = () => {
	const dispatch = useDispatch();
	const [selectedCompanyId, setSelectedCompanyId] = useState(null);
	const [companies, setCompanies] = useState([]);
	const [loadingCompanies, setLoadingCompanies] = useState(true);

	const breadcrumbLinks = [
		{ to: '#', label: 'Inicio' },
		{ to: '#', label: 'Abastecimiento' },
		{ to: '#', label: 'Panel Gerencia' },
	];

	// Load companies list
	useEffect(() => {
		const fetchCompanies = async () => {
			try {
				setLoadingCompanies(true);
				const response = await getCompanyWithHourMeterList();
				const companiesData = response.map(company => ({
					value: company._id,
					label: company.name,
				}));
				setCompanies(companiesData);
			} catch (error) {
				console.error('Error loading companies:', error);
			} finally {
				setLoadingCompanies(false);
			}
		};

		fetchCompanies();
	}, []);

	// Clear dashboard data on mount
	useEffect(() => {
		const timer = setTimeout(() => {
			dispatch(clearDashboardData());
			dispatch(clearFuelTankData());
		}, 100);

		return () => clearTimeout(timer);
	}, [dispatch]);

	const isMediumOrLarger = useMediaQuery({ minWidth: 768 });
	const legendPosition = isMediumOrLarger ? 'right' : 'bottom';

	// Memoize default filters to prevent unnecessary object recreations
	const hourMeterFilters = useMemo(() => ({ option: 'año' }), []);
	const consumptionsFilters = useMemo(() => ({ option: 'año' }), []);
	const emissionsFilters = useMemo(() => ({ option: 'año' }), []);
	const consumptionsMapFilters = useMemo(() => ({ option: 'año' }), []);

	// Memoize company change handler to avoid unnecessary rerenders
	const handleCompanyChange = useCallback(value => {
		setSelectedCompanyId(value);
	}, []);

	return (
		<React.Fragment>
			<UiContent />
			<div className='page-content mb-10' style={{ paddingBottom: '90px' }}>
				<Container fluid>
					<BreadCrumb
						title={'Panel de Combustible - Gerencia'}
						links={breadcrumbLinks}
					/>
					{/* Global Filters */}
					<Card className='mb-3' style={{ borderRadius: '16px' }}>
						<CardBody className='p-2'>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '12px',
									flexWrap: 'wrap',
								}}
							>
								<span style={{ fontWeight: 500, fontSize: '14px' }}>
									Filtros Globales:
								</span>
								<Select
									placeholder='Todas las Empresas'
									data={companies}
									value={selectedCompanyId}
									onChange={handleCompanyChange}
									clearable
									searchable
									size='xs'
									radius='xl'
									style={{ minWidth: '250px', maxWidth: '350px' }}
									disabled={loadingCompanies}
									styles={{
										input: {
											fontSize: '14px',
										},
									}}
								/>
							</div>
						</CardBody>
					</Card>

					<Grid columns={24}>
						<Grid.Col md={12} sm={24} className='m-0 p-1'>
							<ConsumptionsManagementEChart
								title={'Consumos de Combustible por Establecimiento'}
								legendPosition={legendPosition}
								minHeight={'300px'}
								maxHeight={'500px'}
								defaultFilters={consumptionsFilters}
								companyId={selectedCompanyId}
							/>
						</Grid.Col>
						<Grid.Col md={12} sm={24} className='m-0 p-1'>
							<EmissionsManagementEChart
								title={'Emisiones de Combustible por Establecimiento'}
								legendPosition={legendPosition}
								minHeight={'300px'}
								maxHeight={'500px'}
								defaultFilters={emissionsFilters}
								companyId={selectedCompanyId}
							/>
						</Grid.Col>
						<Grid.Col span={12} className='m-0 p-1'>
							<HourMeterManagementEChart
								title={'Horas de Uso por Establecimiento'}
								minHeight={'300px'}
								maxHeight={'500px'}
								defaultFilters={hourMeterFilters}
								companyId={selectedCompanyId}
							/>
						</Grid.Col>
						<Grid.Col span={12} className='m-0 p-1'>
							<ConsumptionsManagementMap
								title={'Consumos de Combustible por Establecimiento'}
								minHeight={'300px'}
								maxHeight={'500px'}
								defaultFilters={consumptionsMapFilters}
								companyId={selectedCompanyId}
							/>
						</Grid.Col>
					</Grid>
				</Container>
			</div>
		</React.Fragment>
	);
};

export default ManagementFuelDashboard;
